import React, { useEffect } from 'react';
import { useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  TextField,

} from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

const AddUpdateCountryModal = ({ onClose, refetch, countryId = null, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  // Form state
  const [formData, setFormData] = useState({
    countryName: initialData?.country_name || '',
    countryCode: initialData?.country_code || '',
    currencyCode: initialData?.country_currency_code || '',
    currencyName: initialData?.country_currency_name || '',
    telephoneCode: initialData?.telephone_code || ''
  });

  const validate = (values) => {
    const newErrors = {};

    // Country Name validation
    if (!values.countryName.trim()) {
      newErrors.countryName = 'Country name is required';
    } else if (values.countryName.length < 2) {
      newErrors.countryName = 'Country name must be at least 2 characters';
    }

    // Country Code validation
    if (!values.countryCode.trim()) {
      newErrors.countryCode = 'Country code is required';
    } else if (values.countryCode.length > 5) {
      newErrors.countryCode = 'Country code must not exceed 5 characters';
    }

    // Currency Code validation
    if (!values.currencyCode.trim()) {
      newErrors.currencyCode = 'Currency code is required';
    } else if (values.currencyCode.length > 5) {
      newErrors.currencyCode = 'Currency code must not exceed 5 characters';
    }

    // Currency Name validation
    if (!values.currencyName.trim()) {
      newErrors.currencyName = 'Currency name is required';
    }

    // Telephone Code validation
    if (!values.telephoneCode.trim()) {
      newErrors.telephoneCode = 'Telephone code is required';
    } else if (values.telephoneCode.length > 5) {
      newErrors.telephoneCode = 'Telephone code must not exceed 5 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        countryId: countryId || 0,
        ...formData
      };
      const response = await api.post('/admin/CreateUpdateCountry', payload);
      if (response.data.status == '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }

      onClose();
      refetch?.();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="typeCode" className="text-sm font-medium">
              Country Name
            </label>
            <TextField
              size="small"
              id="countryName"
              name="countryName"
              value={formData.countryName}
              onChange={handleInputChange}
              placeholder="Enter country name"
              disabled={isSubmitting}
              error={Boolean(errors.countryName)}
              className={'w-full'}
            />
            {errors.countryName && (
              <p className="text-sm text-red-500 mt-1">{errors.countryName}</p>
            )}
          </div>


          <div className="space-y-2">
            <label htmlFor="countryCode" className="text-sm font-medium block">Country Code</label>
            <TextField
              size='small'
              id="countryCode"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleInputChange}
              placeholder="Enter country code (max 5 characters)"
              maxLength={5}
              disabled={isSubmitting}
              error={Boolean(errors.countryCode)}
              className={'w-full'}
            />
            {errors.countryCode && (
              <p className="text-sm text-red-500 mt-1">{errors.countryCode}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="currencyCode" className="text-sm font-medium block">Currency Code</label>
            <TextField
              size='small'
              id="currencyCode"
              name="currencyCode"
              value={formData.currencyCode}
              onChange={handleInputChange}
              placeholder="Enter currency code (max 5 characters)"
              maxLength={5}
              disabled={isSubmitting}
              error={Boolean(errors.currencyCode)}
              className={'w-full'}
            />
            {errors.currencyCode && (
              <p className="text-sm text-red-500 mt-1">{errors.currencyCode}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="currencyName" className="text-sm font-medium block">Currency Name</label>
            <TextField
              size='small'
              id="currencyName"
              name="currencyName"
              value={formData.currencyName}
              onChange={handleInputChange}
              placeholder="Enter currency name"
              disabled={isSubmitting}
              error={Boolean(errors.currencyName)}
              className={'w-full'}
            />
            {errors.currencyName && (
              <p className="text-sm text-red-500 mt-1">{errors.currencyName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="telephoneCode" className="text-sm font-medium block">Telephone Code</label>
            <TextField
              size='small'
              id="telephoneCode"
              name="telephoneCode"
              value={formData.telephoneCode}
              onChange={handleInputChange}
              placeholder="Enter telephone code (max 5 characters)"
              maxLength={5}
              disabled={isSubmitting}
              error={Boolean(errors.telephoneCode)}
              className={'w-full'}
            />
            {errors.telephoneCode && (
              <p className="text-sm text-red-500 mt-1">{errors.telephoneCode}</p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={countryId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateCountryModal;